'use client';

import { useCallback, useRef, useState } from 'react';

import type { RouterInputs } from '@poseidon/api/types';
import { ClickToCallButtonPure } from '@poseidon/components/Softphone/ClickToCallButtonPure';
import { createSoftphoneClient } from '@poseidon/components/Softphone/createSoftphoneClient';
import type { SoftphoneClient, SoftphoneProvider } from '@poseidon/components/Softphone/softphoneClient.types';
import { useSoftphone } from '@poseidon/components/Softphone/useSoftphone';

import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { api } from '@/utils/api';

type CallOutcome = RouterInputs['voiceCall']['logOutcome']['outcome'];

const CALL_OUTCOMES: ReadonlyArray<{ value: CallOutcome; label: string }> = [
  { value: 'connected', label: 'Connected' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'callback', label: 'Callback' },
  { value: 'other', label: 'Other' },
];

function isCallOutcome(value: string): value is CallOutcome {
  return CALL_OUTCOMES.some((o) => o.value === value);
}

export interface ClickToCallButtonProps {
  teamId: string;
  /** Public contact id (contact_public_id). */
  contactId: string;
  contactName?: string;
  phoneNumber?: string | null;
  /** Team feature flag — when false the affordance is hidden entirely. */
  enabled: boolean;
  /**
   * Whether the rep has a caller number provisioned. When false (and `enabled` is true), the
   * button renders greyed/disabled with a tooltip instead of disappearing.
   */
  callingConnected: boolean;
  /** The team's carrier; picks the matching softphone client (Telnyx vs Twilio). */
  provider: SoftphoneProvider;
  /**
   * When placing the call for a cadence `call` step, the cadence_step id. Threaded to
   * `startCall` so logging the outcome completes that step server-side.
   */
  cadenceStepId?: string;
  /** Called after a call outcome is logged (e.g. to invalidate the contact / reminder caches). */
  onCallLogged?: () => void;
  className?: string;
}

/**
 * Dashboard click-to-call entry point. Wires the tRPC voice routes to the shared, provider-
 * agnostic `ClickToCallButtonPure`. The carrier is chosen server-side and passed in as
 * `provider`, so this button is the same for Telnyx and Twilio — only the client differs.
 */
export function ClickToCallButton({
  teamId,
  contactId,
  contactName,
  phoneNumber,
  enabled,
  callingConnected,
  provider,
  cadenceStepId,
  onCallLogged,
  className,
}: ClickToCallButtonProps) {
  const [open, setOpen] = useState(false);

  const getTokenMutation = api.voiceCall.getClientToken.useMutation();
  const startCallMutation = api.voiceCall.startCall.useMutation();
  const logOutcomeMutation = api.voiceCall.logOutcome.useMutation();

  // One client per mounted button (matching the team's carrier); useSoftphone owns its
  // connect/teardown lifecycle.
  const clientRef = useRef<SoftphoneClient | null>(null);
  if (clientRef.current === null) clientRef.current = createSoftphoneClient(provider);
  const client = clientRef.current;

  const fetchToken = useCallback(async () => {
    const res = await getTokenMutation.mutateAsync({ teamId });
    return { token: res.token };
  }, [getTokenMutation, teamId]);

  const startServerCall = useCallback(async () => {
    const res = await startCallMutation.mutateAsync({ teamId, contactId, stepId: cadenceStepId });
    return {
      voiceCallId: res.voiceCallId,
      destinationNumber: res.destinationNumber,
      callerNumber: res.callerNumber,
      recordingEnabled: res.recordingEnabled,
    };
  }, [startCallMutation, teamId, contactId, cadenceStepId]);

  const controller = useSoftphone({ client, fetchToken, startServerCall });

  const handleCall = useCallback(() => {
    setOpen(true);
    void controller.start();
  }, [controller]);

  const handleClose = useCallback(() => {
    controller.hangup();
    setOpen(false);
  }, [controller]);

  const handleSelectOutcome = useCallback(
    async (outcome: string) => {
      const voiceCallId = controller.voiceCallId;
      if (voiceCallId && isCallOutcome(outcome)) {
        await updateWithToast({
          loading: 'Saving call outcome…',
          success: 'Call logged',
          mutationFnAsync: () => logOutcomeMutation.mutateAsync({ teamId, voiceCallId, outcome }),
        });
        onCallLogged?.();
      }
      setOpen(false);
    },
    [controller.voiceCallId, logOutcomeMutation, teamId, onCallLogged],
  );

  return (
    <ClickToCallButtonPure
      enabled={enabled}
      callingConnected={callingConnected}
      phoneNumber={phoneNumber}
      contactName={contactName}
      open={open}
      onCall={handleCall}
      onClose={handleClose}
      status={controller.status}
      durationSec={controller.durationSec}
      isMuted={controller.isMuted}
      error={controller.error}
      isRecording={controller.isRecording}
      outcomes={CALL_OUTCOMES}
      onToggleMute={controller.toggleMute}
      onHangup={controller.hangup}
      onSelectOutcome={(outcome) => void handleSelectOutcome(outcome)}
      className={className}
    />
  );
}
